import { RootViewModel } from './root-view-model';
import { MasterViewModel } from './master-view-model';
import { AggregateMetaData } from '../meta-data/aggregate-meta-data';
import { ExternalMetaData, InternalCollectionMetaData } from '../meta-data/domain-model-meta-data';
import { CollectionViewModel } from './collection-view-model';
import { CollectionChangedEventArgs } from './collection-changed-event-args';
import { MasterDetailRootViewModel } from './master-detail-root-view-model';
import { SnapShotListViewModel } from './snap-shot-list-view-model';
import { ClassConstructor } from '@nts/std/serialization';
import type { CollectionViewModelInterface, CoreOrchestratorViewModelInterface, CreateCustomExternalViewModelInterface, CreateInternalViewModelInterface, DomainModelCollectionInterface, ExternalViewModelInterface, IdentityInterface, InternalViewModelInterface, ItemViewModelInterface, MasterDetailOrchestratorViewModelInterface, MasterViewModelInterface, ModelInterface, OrchestratorViewModelInterface, SnapShotFrameIdentityInterface, SnapShotFrameInterface } from '@nts/std/interfaces';
import { CustomExternalViewModelInspector } from './decorators';
import { MetaDataFactory } from '../meta-data/meta-data-factory';
import { ExternalInspector } from '../domain-models';
import { MetaDataUtils } from '../meta-data';
import { CollectionChangedAction } from '@nts/std/types';
import type { CreateItemViewModelArguments } from '@nts/std/interfaces';
import { takeUntil } from 'rxjs';

export class ViewModelFactory {

    static async createItemViewModel<
        TItemViewModel extends ItemViewModelInterface<TIdentity, TModel>,
        TModel extends ModelInterface<TIdentity>,
        TIdentity extends IdentityInterface>({
            itemViewModelType,
            domainModel,
            aggregateMetaData,
            orchestratorViewModel,
            ownerCollectionVM,
            isNewItem,
            path,
            isMock,
            skipPostInit = false,
            itemModelType = undefined,
        }: CreateItemViewModelArguments<TIdentity, TModel, TItemViewModel>): Promise<TItemViewModel> {

        const rvm = new itemViewModelType() as TItemViewModel;

        await rvm.preInit();
        await rvm.initItemViewModel(domainModel, aggregateMetaData, orchestratorViewModel, ownerCollectionVM, isNewItem, path, isMock, itemModelType);
        if (!skipPostInit) {
            await rvm.postInit();
        }

        if (ownerCollectionVM) {
            rvm.viewModelChanged.pipe(takeUntil(rvm.destroySubscribers$)).subscribe((name) => {
                ownerCollectionVM.collectionChanged.emit(new CollectionChangedEventArgs(CollectionChangedAction.edit, [rvm], [], [name]));
            });
        }

        return rvm;
    }

    static async createCollectionViewModel<TCollectionViewModelType extends CollectionViewModelInterface<any>>(
        collectionViewModelType: any,
        collection: DomainModelCollectionInterface,
        aggregateMetaData: AggregateMetaData,
        orchestratorViewModel: CoreOrchestratorViewModelInterface,
        internalCollectionMetaData: InternalCollectionMetaData,
        path: string,
        isMock = false,
        itemModelType: ClassConstructor<ModelInterface> = null
    ): Promise<TCollectionViewModelType> {

        const ivm: TCollectionViewModelType = new collectionViewModelType();

        if (!(ivm instanceof CollectionViewModel)) {
            throw new Error(
                `${ivm.constructor.name} must be an instance of CollectionViewModel, check constructor implementation of ${collectionViewModelType}`);
        }
        await ivm.preInit();
        await ivm.init(
            collection,
            aggregateMetaData,
            orchestratorViewModel,
            internalCollectionMetaData,
            path,
            isMock,
            itemModelType
        );
        await ivm.postInit();
        return ivm;
    }

    static async createInternalViewModel<
        TIdentity extends IdentityInterface = IdentityInterface,
        TModel extends ModelInterface<TIdentity> = ModelInterface<TIdentity>,
        TViewModel extends InternalViewModelInterface<TIdentity, TModel> = InternalViewModelInterface<TIdentity, TModel>
    >({
        internalViewModelType,
        internalViewModelTypeArgs = undefined,
        internalModel,
        aggregateMetaData,
        orchestratorViewModel,
        relationMetadata,
        skipPostInit = false,
        path = '',
        isMock = false,
        parent = undefined,
        internalModelType = undefined
    }: CreateInternalViewModelInterface<TIdentity, TModel, TViewModel>): Promise<TViewModel> {
        let ivm: TViewModel;
        if (internalViewModelTypeArgs) {
            ivm = new internalViewModelType(...internalViewModelTypeArgs);
        } else {
            ivm = new internalViewModelType();
        }
        await ivm.preInit();
        await ivm.initInternalViewModel({
            domainModel: internalModel,
            aggregateMetaData,
            orchestratorViewModel,
            relationMetaData: relationMetadata,
            path,
            isMock,
            parent,
            domainModelType: internalModelType
        }

        );
        if (!skipPostInit) {
            await ivm.postInit();
        }
        return ivm;
    }

    static async createExternalViewModel<TIdentity extends IdentityInterface, TModel extends ModelInterface<TIdentity>>(
        externalViewModelType: any,
        externalDomainModel: TModel,
        aggregateMetadata: AggregateMetaData,
        orchestratorViewModel: CoreOrchestratorViewModelInterface,
        externalMetaData: ExternalMetaData,
        path: string,
        isMock: boolean = false,
        externalDomainModelType: ClassConstructor<TModel> = null,
        overrideDecoratorData: ExternalMetaData = null,
        processedNulledModel: string[] = []
    ): Promise<ExternalViewModelInterface<TIdentity, TModel>> {

        // Eredito useMessageResourceKey dall'aggregato padre
        externalMetaData.dependentAggregateMetaData.useMessageResourceKey = externalMetaData.isExtension ? false : aggregateMetadata.useMessageResourceKey;

        const ivm: ExternalViewModelInterface<TIdentity, TModel> = new externalViewModelType();
        await ivm.preInit();
        await ivm.initExternalViewModel(
            externalDomainModel,
            aggregateMetadata,
            orchestratorViewModel,
            externalMetaData,
            path,
            isMock,
            externalDomainModelType,
            overrideDecoratorData,
            processedNulledModel
        );
        await ivm.postInit();
        return ivm;
    }

    static createCustomExternalViewModel({
        propertyNameInPascalCase,
        ownerInstanceClass,
        externalDomainModel,
        externalRetriever,
        eventDispatcher
    }: CreateCustomExternalViewModelInterface): ExternalViewModelInterface {

        const propertyNameInCamelCase = MetaDataUtils.toCamelCase(propertyNameInPascalCase);
        const hasCustomExternalViewModelDecorator = CustomExternalViewModelInspector.isApplied(ownerInstanceClass, propertyNameInCamelCase);

        if (!hasCustomExternalViewModelDecorator) {
            throw new Error(`CustomExternalViewModelDecorator not applied to ${propertyNameInCamelCase}`);
        }

        const hasExternalDecorator = ExternalInspector.isApplied(ownerInstanceClass, propertyNameInCamelCase);
        if (!hasExternalDecorator) {
            throw new Error(`ExternalDecorator not applied to ${propertyNameInCamelCase}`);
        }

        const externalViewModelInfo = CustomExternalViewModelInspector.getValue(ownerInstanceClass, propertyNameInCamelCase);
        const externalMetaData: ExternalMetaData = new ExternalMetaData();
        const dependentAggregateMetaData = MetaDataFactory.buildAggregateMetaDataFromClass(externalViewModelInfo.modelType, new externalViewModelInfo.modelType());
        externalMetaData.dependentAggregateMetaData = dependentAggregateMetaData;

        const externalInfo = ExternalInspector.getValue(ownerInstanceClass, propertyNameInCamelCase);
        externalMetaData.isRequired = externalInfo.isRequired;
        externalMetaData.descriptions = externalInfo.descriptions;
        externalMetaData.principalPropertyName = propertyNameInPascalCase;

        const evm: ExternalViewModelInterface = new externalViewModelInfo.viewModelType();
        evm.initCustomExternalViewModel(
            externalDomainModel,
            externalMetaData,
            externalViewModelInfo.modelType,
            externalRetriever,
            eventDispatcher
        );
        return evm;
    }

    static async createMasterViewModel<
        TRootViewModel extends MasterDetailRootViewModel<TModel, TIdentity>,
        TViewModel extends MasterViewModel<TRootViewModel, TModel, TIdentity>,
        TModel extends ModelInterface<TIdentity>,
        TIdentity extends IdentityInterface>(
            masterViewModelType: any,
            domainModel: TModel,
            aggregateMetadata: AggregateMetaData,
            orchestratorViewModel: MasterDetailOrchestratorViewModelInterface<TRootViewModel, MasterViewModelInterface<TIdentity, TModel, TRootViewModel>>,
            rootDomainModelType: any,
            skipPostInit = false
        ): Promise<TViewModel> {
        const mvm: TViewModel = new masterViewModelType() as TViewModel;
        await mvm.preInit();
        await mvm.initMasterViewModel(domainModel, aggregateMetadata, orchestratorViewModel, rootDomainModelType);
        if (!skipPostInit) {
            await mvm.postInit();
        }
        return mvm;
    }

    static async createSnapShotListViewModel<
        TViewModel extends SnapShotListViewModel<TModel, TIdentity>,
        TModel extends SnapShotFrameInterface<TIdentity>,
        TIdentity extends SnapShotFrameIdentityInterface>(
            listViewModelType: any,
            domainModel: TModel,
            aggregateMetadata: AggregateMetaData,
            orchestratorViewModel: OrchestratorViewModelInterface,
            rootDomainModelType: any,
            skipPostInit = false
        ): Promise<TViewModel> {
        const mvm: TViewModel = new listViewModelType() as TViewModel;
        await mvm.preInit();
        await mvm.init(
            domainModel,
            aggregateMetadata,
            orchestratorViewModel,
            rootDomainModelType
        );
        if (!skipPostInit) {
            await mvm.postInit();
        }
        return mvm;
    }

    static async createRootViewModel<
        TViewModel extends RootViewModel<TModel, TIdentity>,
        TModel extends ModelInterface<TIdentity>,
        TIdentity extends IdentityInterface>(
            rootViewModelType: any,
            domainModel: TModel,
            aggregateMetadata: AggregateMetaData,
            orchestratorViewModel: CoreOrchestratorViewModelInterface,
            skipPostInit = false,
            rootDomainModelType = null
        ): Promise<RootViewModel<TModel, TIdentity>> {

        const rvm: TViewModel = new rootViewModelType() as TViewModel;
        await rvm.preInit();
        await rvm.initRootViewModel(domainModel, aggregateMetadata, orchestratorViewModel, undefined, undefined, undefined, undefined, rootDomainModelType);
        if (!skipPostInit) {
            await rvm.postInit();
        }
        return rvm;

    }
}
